import { Controller } from "@hotwired/stimulus"

// Polls the discovery_status endpoint for a jurisdiction and
// reloads the page when the project count ticks up — so a user
// who hits a scan with no immediate results doesn't have to
// manually refresh. The pulsing scan bar below is the visible
// "we're working" signal while they wait.
//
// Markup contract:
//   <div data-controller="gs-discovery-monitor"
//        data-gs-discovery-monitor-url-value="/gridsignal/jurisdictions/17/discovery_status"
//        data-gs-discovery-monitor-initial-count-value="0">
//     <div class="gs-scan-bar">…</div>
//     <p data-gs-discovery-monitor-target="status">Scanning the wider market…</p>
//   </div>
//
// Polls every 5s, times out after 2min of no change. Replaces the
// static "refresh in a minute" copy with a live signal.
export default class extends Controller {
  static targets = ["status", "elapsed"]
  static values = {
    url:          String,
    initialCount: Number,
    pollInterval: { type: Number, default: 5000 },
    maxDurationMs:{ type: Number, default: 120000 }
  }

  connect() {
    this.startedAt = Date.now()
    this.schedule()
    this.tickElapsed()
    this.elapsedTimer = setInterval(() => this.tickElapsed(), 1000)
  }

  disconnect() {
    clearTimeout(this.pollTimer)
    clearInterval(this.elapsedTimer)
  }

  schedule() {
    this.pollTimer = setTimeout(() => this.poll(), this.pollIntervalValue)
  }

  async poll() {
    if (Date.now() - this.startedAt > this.maxDurationMsValue) {
      this.stopWithMessage("We're still looking — refresh when you're ready to see the latest.")
      return
    }

    try {
      const resp = await fetch(this.urlValue, {
        headers: { "Accept": "application/json" },
        credentials: "same-origin"
      })
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`)
      const data = await resp.json()

      // Only reload when the project count has genuinely INCREASED
      // beyond what the page was rendered with. Don't reload on
      // has_data alone — that goes true as soon as any projects
      // exist, which creates an infinite reload loop when the page
      // already shows projects but is still polling for others.
      if (data.project_count > this.initialCountValue) {
        this.stopWithMessage("Results are ready — loading…")
        setTimeout(() => window.location.reload(), 400)
        return
      }
    } catch (err) {
      // Transient failures are fine — just keep polling.
    }

    this.schedule()
  }

  stopWithMessage(message) {
    clearTimeout(this.pollTimer)
    if (this.hasStatusTarget) this.statusTarget.textContent = message
  }

  tickElapsed() {
    if (!this.hasElapsedTarget) return
    const seconds = Math.floor((Date.now() - this.startedAt) / 1000)
    this.elapsedTarget.textContent = `${seconds}s`
  }
}
